import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { logAnalyticsEvent } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { GlassCard } from '@/components/ui/GlassCard';
import { HeroBackground } from '@/components/three';
import { getHostedCheckoutUrl, prefetchHostedCheckout } from '@/lib/hostedCheckoutPrefetch';
import { metaInitiateCheckout } from '@/lib/metaPixel';
import { isPhoneDevice } from '@/hooks/useIsMobile';
import { PLAN_CONFIGS, PlanId } from '@shared/types';

// Where a buyer lands when they back out of Stripe. It used to be the four-tier
// pricing grid — the same ladder they had already walked away from once, at the
// same prices, with the same "Choose your plan" heading. This is the one screen
// in the funnel that is allowed to be a different offer, so it is ONE card, one
// price, one button, and nothing else. No subscription sits beside it: putting
// $49/mo back on screen re-opens exactly the comparison they just abandoned.
//
// The price differs by device, and both halves are real prices this page can
// charge. A phone gets the discounted $59 SKU; a desktop gets $79, which is what
// Lifetime has always cost.

// The struck-through number, per device. Both are read from PLAN_CONFIGS rather
// than typed, so a reprice moves the anchor with the price.
//
// On desktop the anchor is STARTER'S MONTHLY RATE, and it is labelled as such
// rather than left as a bare "was". A strikethrough with nothing next to it
// claims this plan used to cost that, which is false - Lifetime has never been
// billed monthly. Labelled, it is a true comparison: the alternative on offer
// really is $49 every month, and this is $79 one time.
// The quota anchor is Starter's on BOTH devices: it is the only "old number"
// that means anything here. The mobile price anchor is the $79 Lifetime, but
// its quotas are identical to the $59 SKU's, so anchoring quotas against it
// would print the same number twice.
const STARTER_QUOTAS = PLAN_CONFIGS.starter.quotas;

function anchorFor(phone: boolean): { label: string; note: string | null } {
  return phone
    ? { label: `$${PLAN_CONFIGS.lifetime.price}`, note: null }
    : {
        label: `$${PLAN_CONFIGS.starter.price}/mo`,
        note: `That is what Starter costs every month. This is $${PLAN_CONFIGS.lifetime.price}, once.`,
      };
}

export function OfferPage() {
  // Frozen at mount: isPhoneDevice() is synchronous and UA-based, so it cannot
  // flip between the price the card renders and the price the button charges.
  const [phone] = useState(() => isPhoneDevice());
  const [selected] = useState<PlanId>(() => (phone ? 'lifetime_offer' : 'lifetime'));
  const anchor = anchorFor(phone);
  const plan = PLAN_CONFIGS[selected];
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    logAnalyticsEvent('exit_offer_viewed', { plan_id: selected });
    // Lifetime is the preselected card and always checks out through hosted, so
    // its session can be minted while they read.
    prefetchHostedCheckout(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async () => {
    logAnalyticsEvent('begin_checkout', {
      plan_id: selected,
      flow: 'exit_offer',
      currency: 'USD',
      value: PLAN_CONFIGS[selected].price,
      items: [{ item_id: selected, item_name: PLAN_CONFIGS[selected].name, price: PLAN_CONFIGS[selected].price }],
    });

    // Lifetime goes to hosted Checkout on EVERY device, and that is deliberate.
    // /checkout?plan=lifetime is blocked on desktop (blockDesktopLifetime), so
    // routing this through the embedded page would bounce a desktop buyer to the
    // home page mid-purchase. Sending it to Stripe directly also keeps the
    // one-time SKU out of the embedded Payment Element path, which is built
    // around subscriptions.
    //
    // NOTE, on the record: this makes Lifetime purchasable from a desktop for the
    // first time. That is the point of the offer, but it is a real change to who
    // can buy the $79 one-time plan.
    {
      setLoading(true);
      try {
        const sessionUrl = await getHostedCheckoutUrl(selected);
        metaInitiateCheckout(selected, PLAN_CONFIGS[selected].name, Math.round(PLAN_CONFIGS[selected].price * 100));
        window.location.href = sessionUrl;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start checkout';
        toast.error(message);
        setLoading(false);
      }
    }
  };

  return (
    <HeroBackground>
      <div className="relative z-10">
        <nav
          className="fixed top-0 left-0 right-0 z-50"
          style={{
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden">
                <img src="/wolfromulus.png" alt="Romulus" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-2xl font-bold text-white font-roman tracking-wider">ROMVLVS</span>
            </Link>
            <Link
              to="/pricing"
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              See all plans
            </Link>
          </div>
        </nav>

        <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 pt-24 pb-12">
          <div
            className={`w-full max-w-4xl transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full border border-primary-500/40 bg-primary-500/10 text-[10px] font-bold tracking-[0.2em] uppercase text-primary-300 mb-4">
                Before you go
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-4"
                style={{ textShadow: '0 0 60px rgba(91,58,58,0.3)' }}
              >
                One-time offer
              </h1>
            </div>

            <div
              className="max-w-md mx-auto mb-10"
              style={{ animation: 'fadeInUp 0.8s ease-out 0.3s both' }}
            >
              <GlassCard className="relative ring-2 ring-primary-500 border-primary-500/50 shadow-[0_0_40px_rgba(91,58,58,0.3)]">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-xs font-semibold text-white shadow-lg flex items-center gap-1 whitespace-nowrap">
                    <Crown className="w-3 h-3" />
                    NO MONTHLY BILL
                  </span>
                </div>

                <div className="flex flex-col h-full">
                  <div className="mb-4">
                    {/* No radio. There is one card, so there is nothing to pick. */}
                    <h3 className="text-lg font-semibold text-white font-display mb-2">{plan.name}</h3>
                    <p className="text-xs text-gray-400 italic">{plan.tagline}</p>
                  </div>

                  <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                    <span className="text-2xl font-bold text-gray-600 line-through tabular-nums">
                      {anchor.label}
                    </span>
                    <span className="text-4xl font-bold text-white">
                      <span className="text-primary-400 text-2xl">$</span>
                      {plan.price}
                    </span>
                    {/* "once", never "/month". The whole offer is that this one
                        never bills again. */}
                    <span className="text-sm text-gray-400">once</span>
                  </div>
                  {/* Says what the struck-through number IS. Without this the
                      strikethrough claims Lifetime used to be billed monthly,
                      which it never has been. */}
                  {anchor.note && (
                    <p className="text-[11px] text-gray-500 mb-4">{anchor.note}</p>
                  )}
                  {!anchor.note && <div className="mb-4" />}

                  {/* Both allowances, with the units on BOTH sides.
                      Starter's numbers refill every month; this one does not
                      refill at all - so "75 / mo" struck through next to "1,000"
                      would be a straightforwardly misleading comparison without
                      the "/ mo" and "total" labels and the line underneath. They
                      are not optional decoration. */}
                  <div className="mb-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex justify-between items-baseline text-sm mb-2">
                      <span className="text-gray-400">Leads</span>
                      <span className="flex items-baseline gap-2">
                        <span className="text-gray-600 line-through tabular-nums">
                          {STARTER_QUOTAS.leadsLimit.toLocaleString()} / mo
                        </span>
                        <span className="text-white font-semibold tabular-nums">
                          {plan.quotas.leadsLimit.toLocaleString()} total
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-gray-400">Websites</span>
                      <span className="flex items-baseline gap-2">
                        <span className="text-gray-600 line-through tabular-nums">
                          {STARTER_QUOTAS.sitesLimit.toLocaleString()} / mo
                        </span>
                        <span className="text-white font-semibold tabular-nums">
                          {plan.quotas.sitesLimit.toLocaleString()} total
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Says the quiet part out loud, because the strikethrough above
                      implies an upgrade and the honest answer is "bigger bucket,
                      but it is a bucket". Starter's allowance comes back every
                      month; this one is handed over once. */}
                  <p className="text-[11px] text-gray-500 mb-4 leading-snug">
                    Starter&apos;s allowance refills monthly. This one does not — it is{' '}
                    <span className="text-gray-300">
                      {plan.quotas.leadsLimit.toLocaleString()} leads and{' '}
                      {plan.quotas.sitesLimit.toLocaleString()} sites in total
                    </span>
                    , yours to spend at whatever pace you like, with nothing to renew.
                  </p>

                  <ul className="space-y-2 flex-1">
                    {plan.features.slice(2).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                        <div className="w-4 h-4 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-2.5 h-2.5 text-primary-400" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </GlassCard>
            </div>

            <div
              className="flex flex-col items-center gap-4"
              style={{ animation: 'fadeInUp 0.8s ease-out 0.5s both' }}
            >
              <Button
                size="lg"
                className="w-full max-w-md h-14 text-lg font-semibold group relative overflow-hidden"
                onClick={handleCheckout}
                loading={loading}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {`Get Lifetime · $${plan.price} once`}
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Button>

              <p className="text-gray-500 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors font-medium">
                  Sign in
                </Link>
              </p>

              <p className="text-xs text-gray-500 text-center max-w-md">
                One payment, no renewal. The allowance above is a total, not a monthly reset.
              </p>

              <p className="text-[11px] text-gray-600 text-center flex items-center justify-center gap-2 pt-1">
                <Link to="/terms" className="underline underline-offset-2 hover:text-gray-400">Terms</Link>
                <span aria-hidden="true">·</span>
                <Link to="/privacy" className="underline underline-offset-2 hover:text-gray-400">Privacy</Link>
                <span aria-hidden="true">·</span>
                <a href="mailto:support@romulus.app" className="underline underline-offset-2 hover:text-gray-400">Contact</a>
              </p>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </HeroBackground>
  );
}
